import { xxhash3 } from "hash-wasm";

const INTERNAL_SHARD_PREFIX = "__tpuf_internal.shard";

/**
 * Object-storage namespace prefix used by turbopuffer:
 * `{xxh3_64(org:ns) as u32, base64url}:{org_id}:{namespace}`
 */
export async function namespacePrefix(
  orgId: string,
  namespace: string,
  shard?: { version: number; index: number },
): Promise<string> {
  const route = shard
    ? `${INTERNAL_SHARD_PREFIX}.${namespace}.v${shard.version}.s${shard.index}`
    : namespace;
  const orgAndNamespace = `${orgId}:${route}`;
  const hex = await xxhash3(orgAndNamespace);
  const low32 = Number(BigInt(`0x${hex}`) & 0xffffffffn);
  const bytes = new Uint8Array(4);
  new DataView(bytes.buffer).setUint32(0, low32, true);
  return `${bytesToBase64Url(bytes)}:${orgAndNamespace}`;
}

/** Shard prefixes when both count and version are known; otherwise the unsharded prefix. */
export async function namespacePrefixes(
  orgId: string,
  namespace: string,
  shards: number,
  shardingVersion: number | null,
): Promise<string[]> {
  if (shardingVersion == null || shards <= 0) {
    return [await namespacePrefix(orgId, namespace)];
  }
  return Promise.all(
    Array.from({ length: shards }, (_, index) =>
      namespacePrefix(orgId, namespace, { version: shardingVersion, index }),
    ),
  );
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
